import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { MapPin, Plus, Edit, Trash2, Anchor, Search, X } from 'lucide-react';
import { toast } from 'sonner';

const API = `${window.location.origin}/api`;

const tipoDestinoLabels = {
    porto: 'Porto',
    farol: 'Farol',
    furo: 'Furo',
    ilha: 'Ilha',
    cidade: 'Cidade',
    terminal: 'Terminal',
    outro: 'Outro'
};

const DestinosPage = () => {
    const [destinos, setDestinos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingDestino, setEditingDestino] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const { hasRole } = useAuth();

    const [formData, setFormData] = useState({
        nome: '',
        tipo: 'porto',
        distancia_milhas: '',
        coordenadas_lat: '',
        coordenadas_lon: '',
        observacoes: '',
        ativo: true
    });

    useEffect(() => {
        fetchDestinos();
    }, []);

    const fetchDestinos = async () => {
        try {
            const response = await axios.get(`${API}/destinos`);
            setDestinos(response.data);
        } catch (error) {
            toast.error('Erro ao carregar destinos');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        const data = {
            ...formData,
            distancia_milhas: parseFloat(formData.distancia_milhas),
            coordenadas_lat: formData.coordenadas_lat ? parseFloat(formData.coordenadas_lat) : null,
            coordenadas_lon: formData.coordenadas_lon ? parseFloat(formData.coordenadas_lon) : null
        };
        
        try {
            if (editingDestino) {
                await axios.put(`${API}/destinos/${editingDestino.id}`, data);
                toast.success('Destino atualizado!');
            } else {
                await axios.post(`${API}/destinos`, data);
                toast.success('Destino cadastrado!');
            }
            setDialogOpen(false);
            resetForm();
            fetchDestinos();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erro ao salvar destino');
        }
    };

    const handleEdit = (destino) => {
        setEditingDestino(destino);
        setFormData({
            nome: destino.nome,
            tipo: destino.tipo,
            distancia_milhas: destino.distancia_milhas.toString(),
            coordenadas_lat: destino.coordenadas_lat?.toString() || '',
            coordenadas_lon: destino.coordenadas_lon?.toString() || '',
            observacoes: destino.observacoes || '',
            ativo: destino.ativo
        });
        setDialogOpen(true);
    };

    const handleDelete = async (destino) => {
        if (!window.confirm(`Deseja remover o destino "${destino.nome}"?`)) return;
        
        try {
            await axios.delete(`${API}/destinos/${destino.id}`);
            toast.success('Destino removido!');
            fetchDestinos();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erro ao remover destino');
        }
    };

    const resetForm = () => {
        setFormData({
            nome: '',
            tipo: 'porto',
            distancia_milhas: '',
            coordenadas_lat: '',
            coordenadas_lon: '',
            observacoes: '',
            ativo: true
        });
        setEditingDestino(null);
    };

    const canEdit = hasRole(['admin', 'gestor']);
    const canDelete = hasRole(['admin']);

    // Filtrar destinos com base na busca
    const filteredDestinos = destinos.filter(destino => {
        if (!searchTerm) return true;
        const term = searchTerm.toLowerCase();
        return (
            destino.nome?.toLowerCase().includes(term) ||
            tipoDestinoLabels[destino.tipo]?.toLowerCase().includes(term) ||
            destino.observacoes?.toLowerCase().includes(term)
        );
    });

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-pulse text-cyan-400">Carregando...</div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in" data-testid="destinos-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                    <h1 className="font-heading text-2xl font-bold text-white tracking-tight">
                        Cadastro de Destinos
                    </h1>
                    <p className="text-sm text-slate-400 mt-1">
                        Gerenciamento de destinos de viagem a partir de REICON - Belém/PA
                    </p>
                </div>

                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto">
                    {/* Campo de Busca */}
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Buscar destinos..."
                            className="pl-10 pr-8 w-full sm:w-64 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-cyan-500"
                            data-testid="search-destinos"
                        />
                        {searchTerm && (
                            <button
                                onClick={() => setSearchTerm('')}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                                data-testid="clear-search-destinos"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        )}
                    </div>
                
                    {canEdit && (
                        <Dialog open={dialogOpen} onOpenChange={(open) => {
                            setDialogOpen(open);
                            if (!open) resetForm();
                        }}>
                            <DialogTrigger asChild>
                                <Button 
                                    data-testid="btn-new-destino"
                                    className="bg-cyan-600 hover:bg-cyan-700 text-white rounded-sm"
                                >
                                    <Plus className="w-4 h-4 mr-2" />
                                    Novo Destino
                                </Button>
                            </DialogTrigger>
                            <DialogContent className="bg-slate-900 border-slate-800 rounded-sm max-w-lg">
                                <DialogHeader>
                                    <DialogTitle className="font-heading text-xl text-white">
                                        {editingDestino ? 'Editar Destino' : 'Novo Destino'}
                                    </DialogTitle>
                                </DialogHeader>
                            
                                <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                                    <div className="space-y-2">
                                        <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                            Nome do Destino *
                                        </Label>
                                        <Input
                                            data-testid="input-destino-nome"
                                            value={formData.nome}
                                            onChange={(e) => setFormData({...formData, nome: e.target.value})}
                                            required
                                            placeholder="Ex: Porto de Vila do Conde"
                                        className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm"
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                            Tipo
                                        </Label>
                                        <Select 
                                            value={formData.tipo} 
                                            onValueChange={(value) => setFormData({...formData, tipo: value})}
                                        >
                                            <SelectTrigger className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent className="bg-slate-900 border-slate-800">
                                                {Object.entries(tipoDestinoLabels).map(([value, label]) => (
                                                    <SelectItem key={value} value={value}>{label}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                            Distância (Milhas Náuticas) *
                                        </Label>
                                        <Input
                                            type="number"
                                            step="0.1"
                                            data-testid="input-destino-distancia"
                                            value={formData.distancia_milhas}
                                            onChange={(e) => setFormData({...formData, distancia_milhas: e.target.value})}
                                            required
                                            placeholder="0.0"
                                            className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm"
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                            Latitude (opcional)
                                        </Label>
                                        <Input
                                            type="number"
                                            step="0.000001"
                                            value={formData.coordenadas_lat}
                                            onChange={(e) => setFormData({...formData, coordenadas_lat: e.target.value})}
                                            placeholder="-1.456789"
                                            className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                            Longitude (opcional)
                                        </Label>
                                        <Input
                                            type="number"
                                            step="0.000001"
                                            value={formData.coordenadas_lon}
                                            onChange={(e) => setFormData({...formData, coordenadas_lon: e.target.value})}
                                            placeholder="-48.456789"
                                            className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                        Observações
                                    </Label>
                                    <Textarea
                                        value={formData.observacoes}
                                        onChange={(e) => setFormData({...formData, observacoes: e.target.value})}
                                        placeholder="Observações sobre o destino..."
                                        className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm min-h-[60px]"
                                    />
                                </div>

                                <div className="flex items-center justify-between">
                                    <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                        Ativo
                                    </Label>
                                    <Switch
                                        checked={formData.ativo}
                                        onCheckedChange={(checked) => setFormData({...formData, ativo: checked})}
                                    />
                                </div>

                                <div className="flex gap-3 pt-4">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => {
                                            setDialogOpen(false);
                                            resetForm();
                                        }}
                                        className="flex-1 border-slate-700 text-slate-300 hover:bg-slate-800 rounded-sm"
                                    >
                                        Cancelar
                                    </Button>
                                    <Button
                                        type="submit"
                                        data-testid="btn-save-destino"
                                        className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white rounded-sm"
                                    >
                                        {editingDestino ? 'Atualizar' : 'Cadastrar'}
                                    </Button>
                                </div>
                            </form>
                        </DialogContent>
                    </Dialog>
                )}
                </div>
            </div>

            {/* Info Card */}
            <Card className="bg-slate-800/30 border-slate-700 rounded-sm">
                <CardContent className="flex items-center gap-4 p-4">
                    <div className="p-2 bg-cyan-500/20 rounded-sm">
                        <Anchor className="w-5 h-5 text-cyan-400" />
                    </div>
                    <div>
                        <p className="text-sm text-white font-medium">Origem Fixa: REICON - Belém/PA</p>
                        <p className="text-xs text-slate-400">Todas as distâncias são calculadas a partir deste ponto</p>
                    </div>
                </CardContent>
            </Card>

            {/* Table */}
            <Card className="bg-slate-900/50 border-slate-800 rounded-sm">
                <CardContent className="p-0">
                    {filteredDestinos.length === 0 ? (
                        <div className="p-12 text-center">
                            <MapPin className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                            <p className="text-slate-400">
                                {searchTerm ? 'Nenhum destino encontrado para a busca' : 'Nenhum destino cadastrado'}
                            </p>
                        </div>
                    ) : (
                        <Table className="table-naval">
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Nome</TableHead>
                                    <TableHead>Tipo</TableHead>
                                    <TableHead>Distância</TableHead>
                                    <TableHead>Coordenadas</TableHead>
                                    <TableHead>Status</TableHead>
                                    {canEdit && <TableHead>Ações</TableHead>}
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filteredDestinos.map((destino) => (
                                    <TableRow key={destino.id} data-testid={`destino-row-${destino.id}`}>
                                        <TableCell className="text-white font-medium">
                                            {destino.nome}
                                        </TableCell>
                                        <TableCell>
                                            <span className="px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider rounded-sm badge-info">
                                                {tipoDestinoLabels[destino.tipo]}
                                            </span>
                                        </TableCell>
                                        <TableCell className="text-cyan-400 font-mono">
                                            {destino.distancia_milhas} mn
                                        </TableCell>
                                        <TableCell className="text-slate-400 font-mono text-xs">
                                            {destino.coordenadas_lat && destino.coordenadas_lon 
                                                ? `${destino.coordenadas_lat}, ${destino.coordenadas_lon}`
                                                : '-'
                                            }
                                        </TableCell>
                                        <TableCell>
                                            <span className={`
                                                px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider rounded-sm
                                                ${destino.ativo ? 'badge-success' : 'badge-danger'}
                                            `}>
                                                {destino.ativo ? 'Ativo' : 'Inativo'}
                                            </span>
                                        </TableCell>
                                        {canEdit && (
                                            <TableCell>
                                                <div className="flex gap-2">
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => handleEdit(destino)}
                                                        className="text-slate-400 hover:text-white hover:bg-slate-800"
                                                    >
                                                        <Edit className="w-4 h-4" />
                                                    </Button>
                                                    {canDelete && (
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => handleDelete(destino)}
                                                            className="text-red-400 hover:text-red-300 hover:bg-slate-800"
                                                            data-testid={`btn-delete-destino-${destino.id}`}
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </Button>
                                                    )}
                                                </div>
                                            </TableCell>
                                        )}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

export default DestinosPage;
